import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  CircularProgress
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  Close as CloseIcon,
  FileCopy as CopyIcon,
  Check as CheckIcon
} from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import * as iotAPI from '../../data/iot'

const useStyles = makeStyles(theme => ({
  dialog: {
    minWidth: 400
  },
  activationCode: {
    backgroundColor: theme.palette.grey[100],
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    marginTop: theme.spacing(2)
  },
  copyButton: {
    marginLeft: theme.spacing(1)
  },
  successIcon: {
    color: theme.palette.success.main
  }
}))

export const IoTActivationCodeModal = ({ open, onClose, device, modalityId }) => {
  const classes = useStyles()
  const [activationCode, setActivationCode] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (open && device && !device.activatedAt) {
      fetchActivationCode()
    }
  }, [open, device])

  const fetchActivationCode = async () => {
    setLoading(true)
    setError(null)
    
    try {
      // For now, we'll need to get the activation code from the device data
      // This might need to be enhanced based on the actual API response
      if (device.activationCode) {
        setActivationCode(device.activationCode)
      } else {
        setError('Activation code not found for this device')
      }
    } catch (err) {
      console.error('Error fetching activation code:', err)
      setError('Failed to fetch activation code')
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setActivationCode(null)
    setError(null)
    setCopied(false)
    onClose()
  }

  const copyActivationCode = async () => {
    if (!activationCode) return
    
    try {
      await navigator.clipboard.writeText(activationCode)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy activation code:', err)
    }
  }

  if (!device) return null

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      className={classes.dialog}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Activation Code - {device.deviceId}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error">
            {error}
          </Alert>
        ) : activationCode ? (
          <Box>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Device Type: <strong>{device.deviceType}</strong>
            </Typography>
            
            <Typography variant="body2" color="textSecondary" gutterBottom>
              Location: <strong>{device.location || 'N/A'}</strong>
            </Typography>

            <Typography variant="body2" color="textSecondary" gutterBottom>
              Status: <strong>Not Activated</strong>
            </Typography>

            <Box className={classes.activationCode}>
              <Typography variant="subtitle2" gutterBottom>
                Activation Code:
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" style={{ fontFamily: 'monospace', flex: 1 }}>
                  {activationCode}
                </Typography>
                <IconButton
                  onClick={copyActivationCode}
                  size="small"
                  className={classes.copyButton}
                >
                  {copied ? (
                    <CheckIcon className={classes.successIcon} />
                  ) : (
                    <CopyIcon />
                  )}
                </IconButton>
              </Box>
            </Box>

            <Alert severity="info" style={{ marginTop: 16 }}>
              <Typography variant="body2">
                <strong>Instructions:</strong><br />
                1. Enter this activation code on your IoT device<br />
                2. The device will automatically activate and start sending data<br />
                3. Once activated, this device will appear as "Active" in the list
              </Typography>
            </Alert>
          </Box>
        ) : (
          <Alert severity="warning">
            No activation code available for this device.
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="primary" variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default IoTActivationCodeModal
